import posthog from "posthog-js";

import { isAnalyticsDisabledHost } from "@/lib/analytics-env";

/* ──────────────────────────────────────────────────────────────────────────
 * Client-side PostHog initialization.
 *
 * Runs once on initial page load, before the React tree mounts. Configures:
 *   - Product analytics (autocapture + custom events)
 *   - Feature flags (evaluated client-side; server-side via lib/posthog-server)
 *   - Error tracking (uncaught exceptions + unhandled rejections)
 *   - Session replay (sampled — privacy-aware, no PII masking needed because
 *     we redact financial values at the field level)
 *
 * Why reverse-proxy via /ingest: avoids ad blockers and improves first-party
 * data posture. The rewrites are configured in next.config.ts.
 *
 * Localhost gate: we skip init entirely on localhost so dev sessions don't
 * pollute the production project (see lib/analytics-env). To exclude your own
 * production traffic, visit /internal once per browser — it registers an
 * is_internal flag that the PostHog dashboard filter excludes.
 * ────────────────────────────────────────────────────────────────────────── */

if (
  typeof window !== "undefined" &&
  process.env.NEXT_PUBLIC_POSTHOG_KEY &&
  !isAnalyticsDisabledHost(window.location.hostname)
) {
  posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY, {
    api_host: "/ingest",
    ui_host: process.env.NEXT_PUBLIC_POSTHOG_HOST ?? "https://us.posthog.com",
    capture_pageview: "history_change",
    capture_pageleave: true,
    capture_exceptions: true,
    autocapture: true,
    person_profiles: "identified_only",
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: "[data-sensitive], .nk-numeric",
      // Overrides a misconfigured server-side "Canvas recording quality"
      // value in the PostHog project dashboard. Without this, PostHog logs
      // ` cannot be  greater than max: 1. Using max value instead.` on every
      // page load (the value is clamped anyway — this just silences the
      // noisy warning). PostHog requires a string in the range "0"-"1".
      captureCanvas: {
        canvasQuality: "1.0",
      },
    },
    loaded: (ph) => {
      if (process.env.NODE_ENV === "development") {
        ph.debug();
      }
    },
  });
}

// Surface to the rest of the app via a thin re-export if needed.
export { posthog };

// Forward client-side errors to PostHog Error Tracking.
export const onRouterTransitionStart = () => {
  // Placeholder — Next 16 will call this on every client-side navigation.
  // Use for custom timing instrumentation if needed.
};
